<template>
  <Teleport to="body">
    <Transition name="modal">
      <div class="kakao-channel-overlay" v-if="visible" @click.self="handleDismiss">
        <div class="kakao-channel-popup">

          <div class="popup-icon">💬</div>

          <div class="popup-content">
            <h3 class="title">카카오톡 채널 추가</h3>
            <p class="message">
              보다 채널을 추가하면<br/>
              <strong>공지사항, 업데이트 소식, 이벤트</strong>를<br/>
              빠르게 받아볼 수 있어요!
            </p>
          </div>

          <div class="benefit-list">
            <div class="benefit-item">
              <span class="benefit-icon">📢</span>
              <span class="benefit-text">새로운 기능 업데이트 알림</span>
            </div>
            <div class="benefit-item">
              <span class="benefit-icon">🎁</span>
              <span class="benefit-text">채널 전용 이벤트 참여</span>
            </div>
            <div class="benefit-item">
              <span class="benefit-icon">💬</span>
              <span class="benefit-text">1:1 문의 빠른 응답</span>
            </div>
          </div>

          <div class="popup-buttons">
            <button class="confirm" @click="handleAccept">
              <img src="/imgs/LOGO.png" alt="" class="kakao-icon"/>
              채널 추가하기
            </button>
            <button class="cancel" @click="handleDismiss">
              {{ dismissCount >= 2 ? '관심 없어요' : '다음에 할게요' }}
            </button>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, inject, onMounted } from 'vue'

export default {
  setup() {
    const visible = ref(false)
    const isChannelFollowed = inject('isChannelFollowed')
    const followKakaoChannel = inject('followKakaoChannel')
    const member_info = inject('member_info')

    const STORAGE_KEY_FOLLOWED = 'kakao_channel_followed'
    const STORAGE_KEY_DISMISS = 'kakao_channel_dismiss'

    // dismiss 횟수에 따라 재노출 간격 증가
    const DISMISS_INTERVALS = [3, 7, 14, 30] // 3일, 7일, 14일, 30일
    const dismissCount = ref(0)

    function getDismissInfo() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY_DISMISS)
        if (!raw) return null
        return JSON.parse(raw)
      } catch { return null }
    }

    function saveDismiss() {
      const info = getDismissInfo() || { count: 0 }
      const newCount = info.count + 1
      localStorage.setItem(STORAGE_KEY_DISMISS, JSON.stringify({
        date: new Date().toISOString().slice(0, 10),
        count: newCount
      }))
    }

    function shouldShow() {
      // 이미 팔로우 했으면 안 보여줌
      if (isChannelFollowed.value) return false
      if (localStorage.getItem(STORAGE_KEY_FOLLOWED) === 'true') return false

      // 로그인 안 했으면 안 보여줌
      if (!member_info?.mb_id) return false

      const info = getDismissInfo()
      if (!info) return true // 첫 방문

      dismissCount.value = info.count

      // dismiss 횟수별 재노출 간격
      const intervalIdx = Math.min(info.count - 1, DISMISS_INTERVALS.length - 1)
      const intervalDays = DISMISS_INTERVALS[intervalIdx] || 30

      // '관심 없어요' 3번 이상이면 영구 숨김
      if (info.count >= 4) return false

      // 마지막 dismiss 이후 interval일 지났는지 확인
      const dismissDate = new Date(info.date)
      const now = new Date()
      const diffDays = Math.floor((now - dismissDate) / (1000 * 60 * 60 * 24))

      return diffDays >= intervalDays
    }

    function handleAccept() {
      visible.value = false
      // followKakaoChannel 호출 (App.vue에서 provide된 함수)
      followKakaoChannel()
    }

    function handleDismiss() {
      visible.value = false
      saveDismiss()
    }

    onMounted(() => {
      // 출첵 팝업 등과 겹치지 않게 약간 딜레이
      setTimeout(() => {
        if (shouldShow()) {
          visible.value = true
        }
      }, 3000)
    })

    return {
      visible,
      dismissCount,
      handleAccept,
      handleDismiss
    }
  }
}
</script>

<style scoped>
.kakao-channel-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.kakao-channel-popup {
  background: var(--color-surface-primary, #fff);
  border-radius: 24px;
  padding: 32px 24px 24px;
  width: 100%;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
}

.popup-icon {
  font-size: 48px;
  line-height: 1;
}

.popup-content {
  text-align: center;
}

.popup-content .title {
  font-size: 20px;
  font-weight: 800;
  color: var(--color-text-primary, #111);
  margin: 0 0 8px 0;
}

.popup-content .message {
  font-size: 14px;
  color: var(--color-text-secondary, #666);
  margin: 0;
  line-height: 1.6;
}

.popup-content .message strong {
  color: var(--color-text-primary, #333);
}

/* 혜택 리스트 */
.benefit-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  background: var(--color-surface-secondary, #f8f8f8);
  border-radius: 14px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.benefit-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.benefit-text {
  font-size: 13px;
  color: var(--color-text-secondary, #555);
  font-weight: 500;
}

/* 버튼 */
.popup-buttons {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.popup-buttons .confirm {
  width: 100%;
  padding: 14px;
  background: #FEE500;
  color: #3C1E1E;
  border: none;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.popup-buttons .confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(254, 229, 0, 0.4);
}

.popup-buttons .confirm:active {
  transform: translateY(0);
}

.kakao-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.popup-buttons .cancel {
  width: 100%;
  padding: 12px;
  background: transparent;
  color: var(--color-text-tertiary, #999);
  border: none;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.popup-buttons .cancel:hover {
  background: var(--color-surface-secondary, #f0f0f0);
}

/* 모달 트랜지션 */
.modal-enter-active {
  animation: fadeIn 0.3s ease-out;
}
.modal-enter-active .kakao-channel-popup {
  animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-leave-active {
  animation: fadeOut 0.2s ease-in forwards;
}
.modal-leave-active .kakao-channel-popup {
  animation: scaleOut 0.2s ease-in forwards;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes scaleOut {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
}

@media print {
  .kakao-channel-overlay {
    display: none !important;
  }
}
</style>
